import { RefObject, useImperativeHandle, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Collapse,
  Fieldset,
  Flex,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import type { EtcTicketSummary } from '#/components/ui/tickets/etc/model';
import { ConfirmChargeAmountDialog } from '#/components/ui/tickets/components/ui/ConfirmChargeAmountDialog';
import { TICKET_TYPE_CONFIG } from '../../../config';

type QueryResultOption = 'fee_required' | 'no_fee' | 'query_failed';

interface QueryResult {
  type: QueryResultOption;
}

interface QueryResultFeeRequired extends QueryResult {
  type: 'fee_required';
  amount: number;
}
interface QueryResultNoFee extends QueryResult {
  type: 'no_fee';
}
interface QueryResultQueryFailed extends QueryResult {
  type: 'query_failed';
  reason: string;
}

type FillQueryResultDialogResult =
  | QueryResultFeeRequired
  | QueryResultNoFee
  | QueryResultQueryFailed;

interface FillQueryResultValue extends QueryResult {
  amount: string;
  reason: QueryResultQueryFailed['reason'];
}

const InitValue: FillQueryResultValue = {
  type: 'no_fee',
  amount: '',
  reason: '',
};

type ConfirmResult = FillQueryResultDialogResult | undefined;

type ModalRef = {
  open: (ticket: EtcTicketSummary) => Promise<[ConfirmResult, () => void]>;
  queryResultValue: FillQueryResultValue;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useFillQueryResultDialog = () => {
  const modalRef = useRef<ModalRef>(null);
  const open = (ticket: EtcTicketSummary) => modalRef.current?.open(ticket);
  return { props: { modalRef }, open };
};

export function FillQueryResultDialog({
  modalRef,
}: {
  modalRef: RefObject<ModalRef | null>;
}) {
  const [modelState, setModelState] = useState<
    | {
        ticket: EtcTicketSummary;
        onConfirm: () => void;
        onCancel: () => void;
      }
    | undefined
  >();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [queryResultValue, setQueryResultValue] =
    useState<FillQueryResultValue>(InitValue);

  const resetQueryResult = () => {
    setQueryResultValue(InitValue);
  };

  useImperativeHandle(
    modalRef,
    () => ({
      /**
       * Here use the use ref to preserve state, to let confirm method always get the latest value
       */
      queryResultValue: queryResultValue,
      open: async (ticket: EtcTicketSummary) => {
        const close = () => {
          setModelState(undefined);
          resetQueryResult();
          setIsSubmitting(false);
          setShowConfirm(false);
        };
        const promise = new Promise<ConfirmResult>((resolve) => {
          setModelState({
            ticket,
            onConfirm: () => {
              setIsSubmitting(true);
              const queryResultValue = modalRef.current?.queryResultValue;

              if (!queryResultValue) {
                return;
              }
              const { type, amount, reason } = queryResultValue;

              if (type === 'fee_required') {
                const numberAmount = Number(amount);
                if (Number.isNaN(numberAmount)) {
                  console.error('invalid Number string on amount: ' + amount);
                }

                const validNumberAmount = Number.isNaN(numberAmount)
                  ? 0
                  : numberAmount;
                resolve({ type, amount: validNumberAmount });
                return;
              }
              /**
               * return internal state for submit
               */
              if (type === 'no_fee') {
                resolve({ type });
                return;
              }
              if (type === 'query_failed') {
                resolve({ type, reason });
                return;
              }
            },
            /**
             * resolve undefined means "cancel"
             */
            onCancel: () => {
              resolve(undefined);
              close();
            },
          });
        });

        const result = await promise;

        return [result, close] as const;
      },
    }),
    [modalRef, queryResultValue],
  );

  const setReason = (reason: string) =>
    setQueryResultValue((value) => ({
      ...value,
      reason,
    }));

  if (!modelState) {
    return null;
  }

  const { ticket, onConfirm, onCancel } = modelState;
  const ownerId = ticket.idNumber ?? ticket.taxIdNumber ?? '-';
  const subtitle = `${ticket.account.email}．${TICKET_TYPE_CONFIG['etc-tickets'].name}．${ticket.plateNumber}．${ownerId}`;

  const numberAmount = Number(queryResultValue.amount);
  const confirmAmount = Number.isNaN(numberAmount) ? 0 : numberAmount;

  const handleSubmit = () => {
    if (queryResultValue.type === 'fee_required') {
      setShowConfirm(true);
      return;
    }
    onConfirm();
  };

  return (
    <>
      <Modal
        opened={!!ticket}
        onClose={onCancel}
        title={
          <Stack gap={2}>
            <Text fw={600} size="lg">
              回填查詢結果
            </Text>
            <Text size="sm" c="dimmed">
              {`${ticket.account.email}．${TICKET_TYPE_CONFIG['etc-tickets'].name}．${ticket.plateNumber}．${ownerId}`}
            </Text>
          </Stack>
        }
        size="lg"
      >
        <LoadingOverlay visible={isSubmitting} />
        <Stack gap="md">
          {/* Platform link row */}
          <Group
            justify="space-between"
            bg="gray.0"
            p="sm"
            style={{ borderRadius: 8 }}
          >
            <Text size="sm" c="dimmed">
              線下查繳平台
            </Text>
            <Text size="sm" style={{ fontWeight: 600 }}>
              遠通電收
            </Text>
          </Group>

          {/* Query result block */}
          <Fieldset
            variant="unstyled"
            legend={
              <Text size="sm" fw={500}>
                查詢結果{' '}
                <Text span c="red">
                  *
                </Text>
              </Text>
            }
          >
            <Radio.Group
              value={queryResultValue.type}
              onChange={(type) => {
                setQueryResultValue((value) => ({
                  ...value,
                  type,
                }));
              }}
            >
              <Stack gap="sm">
                {/* 無需繳費 */}
                <Radio value="no_fee" label="無需繳費" />

                {/* 需繳費 */}
                <Stack gap="xs">
                  <Radio value="fee_required" label="需繳費" />
                  <Collapse expanded={queryResultValue.type === 'fee_required'}>
                    <Stack
                      gap="xs"
                      pl="md"
                      ml="xs"
                      style={{
                        borderLeft: '2px solid var(--mantine-color-gray-3)',
                      }}
                    >
                      <NumberInput
                        value={queryResultValue.amount}
                        onChange={(amount) => {
                          if (!amount) {
                            return;
                          }
                          setQueryResultValue((value) => ({
                            ...value,
                            amount: amount.toString(),
                          }));
                        }}
                        label={
                          <Text size="sm">
                            線上可繳金額 (NT$){' '}
                            <Text span c="red">
                              *
                            </Text>
                          </Text>
                        }
                        placeholder="輸入金額"
                        min={1}
                      />
                      <Text size="xs" c="dimmed">
                        送出後系統會即時向用戶發起請款，下一步顯示請款結果。
                      </Text>
                    </Stack>
                  </Collapse>
                </Stack>

                {/* 查詢失敗 */}
                <Stack gap="xs">
                  <Radio
                    value="query_failed"
                    label={'查詢失敗'}
                    description="本票直接結案，系統將寄信引導用戶更新資料"
                  />
                  <Collapse expanded={queryResultValue.type === 'query_failed'}>
                    <Stack
                      gap="xs"
                      pl="md"
                      ml="xs"
                      style={{
                        borderLeft: '2px solid var(--mantine-color-gray-3)',
                      }}
                    >
                      <Textarea
                        label="原因"
                        placeholder="Input placeholder"
                        value={queryResultValue.reason}
                        onChange={(e) => {
                          setReason(e.target.value);
                        }}
                      />
                      <Flex gap="xs">
                        <Badge
                          onClick={() => {
                            setReason(
                              '車號與身份證號／統編不匹配，故無法查詢通行費用',
                            );
                          }}
                        >
                          查不到
                        </Badge>
                        <Badge
                          onClick={() => {
                            setReason(
                              '此車號已被會員帳號綁定，故無法查詢通行費用，需請用戶至遠通解綁。',
                            );
                          }}
                        >
                          Etag 已綁定
                        </Badge>
                      </Flex>
                    </Stack>
                  </Collapse>
                </Stack>
              </Stack>
            </Radio.Group>
          </Fieldset>

          {/* Footer */}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onCancel}>
              取消
            </Button>
            <Button
              disabled={!queryResultValue}
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              送出請款
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmChargeAmountDialog
        opened={showConfirm}
        amount={confirmAmount}
        subtitle={subtitle}
        loading={isSubmitting}
        onBack={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          onConfirm();
        }}
      />
    </>
  );
}
